import { flatten, forEach, isEmpty, values } from "lodash";
import { TAG_ATTRIBUTE_TYPES } from "src/consts/tags";

type AttributeTypeRef = {
  id: string;
  type?: string;
};

type AttributeTypesMap = Record<string, AttributeTypeRef[]>;

const getAttributeValue = ({
  attributeType,
  attributes,
  entityId,
}: {
  attributeType: AttributeTypeRef;
  attributes: Record<string, unknown>;
  entityId?: string | undefined;
}): unknown => {
  switch (attributeType.type) {
    case TAG_ATTRIBUTE_TYPES.ENTITY_ID:
      if (entityId && !attributes[attributeType.id]) {
        return entityId;
      }
      return attributes[attributeType.id];
    default:
      return attributes[attributeType.id];
  }
};

const updateAttributes = ({
  attributes,
  entityId,
  attributeTypes,
  typeKeys,
}: {
  attributes?: Record<string, unknown> | undefined;
  entityId?: string | undefined;
  attributeTypes: AttributeTypesMap;
  typeKeys: string[];
}): Record<string, unknown> => {
  const clonedAttributes = !isEmpty(attributes) ? { ...attributes } : {};
  const attributeGroups: AttributeTypeRef[][] = isEmpty(typeKeys)
    ? values(attributeTypes)
    : typeKeys
        .map((key) => attributeTypes[key])
        .filter((group): group is AttributeTypeRef[] => group != null);
  const types = flatten(attributeGroups);

  forEach(types, (type) => {
    const value = getAttributeValue({
      attributeType: type,
      attributes: clonedAttributes,
      entityId,
    });
    if (value) {
      clonedAttributes[type.id] = value;
    }
  });
  return clonedAttributes;
};

export const getUpdatedAttributes = ({
  attributes,
  entityId,
  tagType,
  typeKeys = [],
}: {
  attributes?: Record<string, unknown> | undefined;
  entityId?: string | undefined;
  tagType?: { attributes?: AttributeTypesMap | null } | null;
  typeKeys?: string[];
}): Record<string, unknown> | undefined => {
  if (!tagType || isEmpty(tagType.attributes)) {
    return attributes;
  }

  return updateAttributes({
    attributes,
    entityId,
    attributeTypes: tagType.attributes,
    typeKeys,
  });
};
